import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Calendar,
  Users,
  AlertTriangle,
  Shield,
  Cloud,
  MapPin,
  Phone,
  Building2,
  Clock,
  CheckCircle,
  Download,
  Printer,
  Trash2,
  Radio,
  HardHat,
  Zap,
  FileText,
  UserCheck,
  Eye,
  Shovel,
  Pencil,
  Mail,
  Send
} from 'lucide-react';
import { api } from '@/utils/api';
import CertBadge from '@/components/CertBadge';
// JHA emergency map & directions (Google Maps JS, capability-gated; static
// nearest-facility fallback is always rendered and never depends on the map).
import { JhaEmergencyMap } from '@/components/JhaEmergencyMap';
import { DueEquipmentChecklist } from '@/components/DueEquipmentChecklist';
// WD-T3 M4 (client): the viewer's tier/id gate which crew rows render their
// own due-equipment checklist (worker tier: ONLY the viewer's own row).
import { effectiveTier, getStoredUser } from '@/utils/roleGate';
import { formatCalendarDate } from '@/utils/calendarDate';
// SOL-04: shared per-crew signature truth (also used by ProjectPacketView) —
// the row-level JHA.signedAt is a server creation stamp, NOT sign truth.
import { jhaSignStatus } from '@/utils/jhaSignStatus';

interface JHA {
  id: number;
  date: string;
  projectId: number;
  project: {
    name: string;
    siteAddress: string;
  };
  createdBy: {
    firstName: string;
    lastName: string;
  };
  // Job Info
  jobInfo?: {
    supervisorName: string;
    siteOwner?: string;
    siteId: string;
    latitude: string;
    longitude: string;
    siteAddress: string;
  };
  // Work
  workDescription?: string;
  scopeOfWork?: string;
  elevatedWork?: boolean;
  // Emergency
  emergencyInfo?: {
    is911Functional?: boolean;
    rescuePlan?: string;
    hospital?: { name: string; address: string; phone: string };
    clinic?: { name: string; address: string; phone: string };
    police?: { name: string; address: string; phone: string; nonEmergency?: string };
    fire?: { name: string; address: string; phone: string; nonEmergency?: string };
    utilities?: {
      electric?: { name: string; phone: string };
      gas?: { name: string; phone: string };
      water?: { name: string; phone: string };
    };
    evacuationPoint?: string;
    communicationPoint?: string;
    contacts?: any[];
  };
  // Weather
  weather?: {
    temperature: string;
    condition: string;
    windSpeed: string;
  };
  // Hazards
  hazards?: {
    physical?: string[];
    health?: string[];
  } | any[];
  // Controls
  controls?: {
    ppe?: string[];
    inspections?: string[];
    additionalConsiderations?: string;
  } | any[];
  // Tasks
  tasks?: Array<{ task: string; hazards: string; controls: string }>;
  // Multi-Employer
  multiEmployerAcknowledged?: boolean;
  // RF Safety
  rfSafety?: {
    plan: string;
    groundMonitorSN: string;
    groundMPE: string;
    structureMonitorSN: string;
    structureMPE: string;
    controlMethods: string[];
  };
  // Civil Work
  civilWork?: {
    electricalDeEnergized: boolean;
    electricalEnergized: boolean;
    is811Notified: string;
    excavationType: string;
    excavationDepth: string;
    caveInControls: string;
  };
  // Crew
  crewMembers?: any[] | any;
  siteVisitors?: string[];
  // Meta
  signedAt: string;
  completionTime?: number;
  createdAt: string;
  equipment?: any;
  emergencyPlan?: string;
  siteConditions?: string;
  // S5: the construction plan auto-linked at creation (if the project had one)
  linkedPlan?: {
    id: number;
    name: string;
    version: number;
    createdAt: string;
  } | null;
  // S7: set when a worker created this JHA in an emergency — foreman review pending
  needsForemanReview?: boolean;
}

// Helper maps for display names
const PHYSICAL_HAZARD_NAMES: Record<string, string> = {
  'slips-trips-falls': 'Slips, Trips, or Falls',
  'electrical': 'Electrical',
  'falling-objects': 'Falling Objects',
  'struck-by': 'Struck By',
  'caught-between': 'Caught In/Between',
  'weather': 'Weather Conditions',
  'heights': 'Working at Heights',
  'rigging': 'Rigging/Hoisting',
  'confined-space': 'Confined Space',
  'vehicular': 'Vehicular Traffic',
};

const HEALTH_HAZARD_NAMES: Record<string, string> = {
  'lifting': 'Lifting Hazards',
  'heat-stress': 'Heat Stress',
  'cold-stress': 'Cold Stress',
  'rf-eme': 'EME/RF Exposure',
  'noise': 'Noise Exposure',
  'dust': 'Dust/Particulates',
  'chemicals': 'Chemical Exposure',
  'biological': 'Biological Hazards',
};

const PPE_NAMES: Record<string, string> = {
  'hard-hat': 'Hard Hats',
  'safety-glasses': 'Safety Glasses',
  'gloves-leather': 'Leather Gloves',
  'gloves-nitrile': 'Nitrile Gloves',
  'gloves-cut': 'Cut Resistant Gloves',
  'steel-toe': 'Steel Toe Boots',
  'high-vis': 'High Visibility Vest',
  'harness': 'Fall Protection Harness',
  'rf-monitor': 'RF Monitors',
  'hearing': 'Hearing Protection',
  'respirator': 'Respirator',
  'face-shield': 'Face Shield',
};

const INSPECTION_NAMES: Record<string, string> = {
  'ppe': 'PPE',
  'rescue-kit': 'Rescue Kit',
  'fire-extinguisher': 'Fire Extinguisher',
  'first-aid': 'First Aid Kit',
  'housekeeping': 'Housekeeping',
  'ground-fault': 'Ground Fault Protection',
  'rigging-equipment': 'Rigging Equipment',
  'fall-protection': 'Fall Protection Equipment',
  'electrical-tools': 'Electrical Tools',
  'vehicles': 'Vehicles',
};

const RF_CONTROL_NAMES: Record<string, string> = {
  'no-action': 'No Action Required',
  'power-reduction': 'Power Reduction',
  'site-shutdown': 'Site Shutdown',
  'time-averaging': 'Time Averaging',
  'rf-suit': 'RF Protective Suit',
  'barriers': 'Physical Barriers',
};

export function JHADetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [jha, setJha] = useState<JHA | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    loadJHA();
  }, [id]);

  const loadJHA = async () => {
    try {
      const response = await api.get(`/jha/${id}`);
      setJha(response.data.data);
    } catch (error) {
      console.error('Error loading JHA:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this Pre-Work Hazard Assessment? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      await api.delete(`/jha/${id}`);
      navigate('/jha');
    } catch (error: any) {
      console.error('Error deleting JHA:', error);
      alert(error.response?.data?.message || 'Failed to delete assessment');
      setDeleting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEmailSend = async () => {
    if (!emailTo.trim()) {
      alert('Please enter an email address');
      return;
    }

    setEmailSending(true);
    try {
      // BP2: real authenticated send — server generates the PDF and reports
      // the actual provider result. No fabricated success.
      await api.post(`/jha/${id}/email`, { to: emailTo.trim() });
      alert(`JHA emailed to ${emailTo.trim()} with the PDF attached.`);
      setShowEmailModal(false);
      setEmailTo('');
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to send email.';
      alert(msg);
    } finally {
      setEmailSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Clock className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading Pre-Work Hazard Assessment...</p>
        </div>
      </div>
    );
  }

  if (!jha) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Assessment not found</p>
          <Link to="/jha" className="text-blue-600 hover:text-blue-800 mt-4 inline-block">
            ← Back to Assessments
          </Link>
        </div>
      </div>
    );
  }

  // Handle both old and new data structures
  const crewMembers = Array.isArray(jha.crewMembers) ? jha.crewMembers : [];
  // WD-T3 M4 (client): viewer identity from the repo-standard stored user —
  // a worker-tier viewer gets a due-equipment checklist ONLY on their own
  // crew row (the server returns only the caller's gear for workers anyway;
  // rendering it on other members' cards was false provenance), and the kit
  // pre-use save is foreman+ capability.
  const viewer = getStoredUser();
  const viewerIsWorker = effectiveTier(viewer.role) === 'CREW_MEMBER';
  const canRecordKitCheck = !viewerIsWorker;
  const tasks = Array.isArray(jha.tasks) ? jha.tasks : [];
  const siteVisitors = Array.isArray(jha.siteVisitors) ? jha.siteVisitors : [];
  
  // Handle hazards - could be old format (array) or new format (object)
  const physicalHazards = jha.hazards && !Array.isArray(jha.hazards) 
    ? (jha.hazards.physical || []) 
    : [];
  const healthHazards = jha.hazards && !Array.isArray(jha.hazards) 
    ? (jha.hazards.health || []) 
    : [];
  const oldHazards = Array.isArray(jha.hazards) ? jha.hazards : [];
  
  // Handle controls - could be old format (array) or new format (object)
  const ppeControls = jha.controls && !Array.isArray(jha.controls) 
    ? (jha.controls.ppe || []) 
    : [];
  const inspectionControls = jha.controls && !Array.isArray(jha.controls) 
    ? (jha.controls.inspections || []) 
    : [];
  const oldControls = Array.isArray(jha.controls) ? jha.controls : [];

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white print:bg-white print:text-black print:border-b-2 print:border-blue-900">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 print:hidden">
            <div className="flex items-center gap-4 min-w-0">
              <Link to="/jha" className="text-blue-200 hover:text-white flex-shrink-0">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold">PRE-WORK HAZARD ASSESSMENT</h1>
                <p className="text-blue-200 mt-1">
                  {formatCalendarDate(jha.date, { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Link
                to={`/jha/${id}/edit`}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-400 transition flex items-center gap-2"
              >
                <Pencil className="h-4 w-4" />
                Edit
              </Link>
              <button
                onClick={() => setShowEmailModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 transition flex items-center gap-2"
              >
                <Mail className="h-4 w-4" />
                Email
              </button>
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-600 transition flex items-center gap-2"
              >
                <Printer className="h-4 w-4" />
                Print
              </button>
              {/* SOL-05: the server (jha.service assertCanMutateJHA + deleteJHA)
                  unconditionally 403s worker-tier deletes, so hide the Delete
                  control from worker-tier viewers — never render a false
                  affordance that can only fail. Foreman+ keep it. The Edit
                  link stays for every viewer: workers may sign through the
                  edit path. */}
              {!viewerIsWorker && (
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              )}
            </div>
          </div>

          {/* Print Header */}
          <div className="hidden print:block">
            <h1 className="text-2xl font-bold text-center">PRE-WORK HAZARD ASSESSMENT</h1>
            <p className="text-center text-gray-600">
              {formatCalendarDate(jha.date)} • By: {jha.createdBy.firstName} {jha.createdBy.lastName}
            </p>
          </div>

          {/* Status Banner */}
          {jha.needsForemanReview ? (
            <div className="bg-amber-500/20 border border-amber-400/30 rounded-lg p-4 flex items-center justify-between print:bg-amber-50 print:border-amber-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/30 flex items-center justify-center print:bg-amber-100">
                  <Clock className="h-6 w-6 text-amber-300 print:text-amber-600" />
                </div>
                <div>
                  <div className="font-semibold text-amber-100 print:text-amber-900" data-testid="awaiting-foreman-review-banner">Awaiting foreman review</div>
                  <div className="text-sm text-amber-200 print:text-amber-700">
                    A foreman should review this JHA before it is treated as complete.
                  </div>
                </div>
              </div>
            </div>
          ) : (
          /* SOL-04: sign truth is per crew member (crewMembers[].signature),
             NOT the row-level JHA.signedAt creation stamp. Green "Signed &
             Complete" only when every crew member actually signed; partial,
             open, unknown, and legacy-roster states show the honest status
             from the shared jhaSignStatus() truth function (same truth the
             My Jobs packet shows). */
          (() => {
            // SOL-04: honest status banner keyed off the shared truth function.
            const signStatus = jhaSignStatus(jha);
            const fullySigned = signStatus === 'signed';
            if (!fullySigned) {
              return (
                <div className="bg-amber-500/20 border border-amber-400/30 rounded-lg p-4 flex items-center justify-between print:bg-amber-50 print:border-amber-200" data-testid="sign-status-banner">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500/30 flex items-center justify-center print:bg-amber-100">
                      <AlertTriangle className="h-6 w-6 text-amber-300 print:text-amber-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-amber-100 print:text-amber-900" data-testid="sign-status-label">{signStatus}</div>
                      <div className="text-sm text-amber-200 print:text-amber-700">
                        This assessment is not complete until every listed crew member has signed.
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return (
          <div className="bg-green-500/20 border border-green-400/30 rounded-lg p-4 flex items-center justify-between print:bg-green-50 print:border-green-200" data-testid="sign-status-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/30 flex items-center justify-center print:bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-300 print:text-green-600" />
              </div>
              <div>
                <div className="font-semibold text-green-100 print:text-green-900" data-testid="sign-status-label">Signed & Complete</div>
                <div className="text-sm text-green-200 print:text-green-700">
                  Signed at {new Date(jha.signedAt).toLocaleTimeString()}
                  {jha.completionTime && (
                    <span className="ml-2">• Completed in {jha.completionTime} min</span>
                  )}
                </div>
              </div>
            </div>
          </div>
            );
          })()
          )}
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8 max-w-5xl">
        <div className="space-y-6">
          
          {/* Job Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:shadow-none print:border-gray-300">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="h-6 w-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Job Information</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-gray-500">Date</label>
                <p className="text-base font-semibold text-gray-900">{formatCalendarDate(jha.date)}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Supervisor/Competent Person</label>
                <p className="text-base font-semibold text-gray-900">
                  {jha.jobInfo?.supervisorName || `${jha.createdBy.firstName} ${jha.createdBy.lastName}`}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Project</label>
                <p className="text-base font-semibold text-gray-900">{jha.project.name}</p>
              </div>
              {jha.jobInfo?.siteId && (
                <div>
                  <label className="text-sm text-gray-500">Site / Job ID</label>
                  <p className="text-base font-semibold text-gray-900">{jha.jobInfo.siteId}</p>
                </div>
              )}
              {jha.jobInfo?.siteOwner && (
                <div>
                  <label className="text-sm text-gray-500">Tower / Property Owner</label>
                  <p className="text-base font-semibold text-gray-900">{jha.jobInfo.siteOwner}</p>
                </div>
              )}
              <div className="md:col-span-2">
                <label className="text-sm text-gray-500">Site Address</label>
                <p className="text-base font-medium text-gray-900">{jha.jobInfo?.siteAddress || jha.project.siteAddress}</p>
              </div>
              {jha.linkedPlan && (
                <div className="md:col-span-2" data-testid="linked-plan">
                  <label className="text-sm text-gray-500">Linked Plan</label>
                  <p className="text-base font-medium text-gray-900">
                    {jha.linkedPlan.name} — version {jha.linkedPlan.version}
                    <Link
                      to={`/construction-plans/${jha.linkedPlan.id}`}
                      className="ml-2 text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      View plan
                    </Link>
                  </p>
                </div>
              )}
              {jha.needsForemanReview && (
                <div className="md:col-span-2" data-testid="needs-review-badge">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-sm font-semibold">
                    Needs foreman review
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    This JHA was created by a crew member in the field — a foreman should review it.
                  </p>
                </div>
              )}
              {(jha.jobInfo?.latitude || jha.jobInfo?.longitude) && (
                <div className="md:col-span-2">
                  <label className="text-sm text-gray-500">Coordinates</label>
                  <p className="text-base font-medium text-gray-900">
                    {jha.jobInfo.latitude}, {jha.jobInfo.longitude}
                  </p>
                </div>
              )}
            </div>

            {/* Work Being Performed */}
            {(jha.scopeOfWork || jha.workDescription) && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Work Being Performed
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {jha.scopeOfWork && (
                    <div>
                      <label className="text-sm text-gray-500">Scope of Work</label>
                      <p className="text-base font-medium text-gray-900">{jha.scopeOfWork}</p>
                    </div>
                  )}
                  {jha.elevatedWork !== undefined && (
                    <div>
                      <label className="text-sm text-gray-500">Elevated Work</label>
                      <p className="text-base font-medium text-gray-900">{jha.elevatedWork ? 'Yes' : 'No'}</p>
                    </div>
                  )}
                </div>
                {jha.workDescription && (
                  <div className="mt-4">
                    <label className="text-sm text-gray-500">Work Description</label>
                    <p className="text-base text-gray-900">{jha.workDescription}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Weather */}
          {jha.weather && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:shadow-none print:border-gray-300">
              <div className="flex items-center gap-3 mb-6">
                <Cloud className="h-6 w-6 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-900">Weather Conditions</h2>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="text-sm text-gray-500">Temperature</label>
                  <p className="text-base font-medium text-gray-900">{jha.weather.temperature}°F</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Condition</label>
                  <p className="text-base font-medium text-gray-900">{jha.weather.condition}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Wind Speed</label>
                  <p className="text-base font-medium text-gray-900">{jha.weather.windSpeed} mph</p>
                </div>
              </div>
            </div>
          )}

          {/* Emergency Map & Directions — static fallback text is always shown;
              the live map renders only when a browser map key is configured. */}
          {jha.projectId != null && (
            <JhaEmergencyMap
              projectId={jha.projectId}
              emergencyInfo={jha.emergencyInfo}
            />
          )}

          {/* Emergency Procedures */}
          {jha.emergencyInfo && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:shadow-none print:border-gray-300">
              <div className="flex items-center gap-3 mb-6">
                <Phone className="h-6 w-6 text-red-600" />
                <h2 className="text-xl font-bold text-gray-900">Emergency Procedures</h2>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-800 font-semibold">
                  911 Functional: {jha.emergencyInfo.is911Functional !== false ? 'Yes' : 'No'}
                </p>
              </div>

              {jha.emergencyInfo.rescuePlan && (
                <div className="mb-6">
                  <label className="text-sm text-gray-500">Site Specific Emergency/Rescue Plan</label>
                  <p className="text-base text-gray-900 mt-1">{jha.emergencyInfo.rescuePlan}</p>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                {jha.emergencyInfo.hospital && (
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <h4 className="font-semibold text-red-900 mb-2">🏥 Local ER/Hospital</h4>
                    <p className="text-sm text-red-800">{jha.emergencyInfo.hospital.name}</p>
                    <p className="text-sm text-red-700">{jha.emergencyInfo.hospital.address}</p>
                    <p className="text-sm text-red-700 font-semibold">{jha.emergencyInfo.hospital.phone}</p>
                  </div>
                )}
                {jha.emergencyInfo.clinic && jha.emergencyInfo.clinic.name && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-2">🩺 Occupational Clinic</h4>
                    <p className="text-sm text-blue-800">{jha.emergencyInfo.clinic.name}</p>
                    <p className="text-sm text-blue-700">{jha.emergencyInfo.clinic.address}</p>
                    {jha.emergencyInfo.clinic.phone ? (
                      <p className="text-sm text-blue-700 font-semibold">{jha.emergencyInfo.clinic.phone}</p>
                    ) : (
                      <p className="text-sm text-blue-700 italic">No verified listing — add contact manually</p>
                    )}
                  </div>
                )}
                {jha.emergencyInfo.police && jha.emergencyInfo.police.name && (
                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                    <h4 className="font-semibold text-indigo-900 mb-2">👮 Police Department</h4>
                    <p className="text-sm text-indigo-800">{jha.emergencyInfo.police.name}</p>
                    <p className="text-sm text-indigo-700">{jha.emergencyInfo.police.address}</p>
                    <p className="text-sm text-indigo-700">Emergency: <span className="font-semibold">{jha.emergencyInfo.police.phone}</span></p>
                    {jha.emergencyInfo.police.nonEmergency != null && (
                      <p className="text-sm text-indigo-700">Non-Emergency: {jha.emergencyInfo.police.nonEmergency}</p>
                    )}
                  </div>
                )}
                {jha.emergencyInfo.fire && jha.emergencyInfo.fire.name && (
                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                    <h4 className="font-semibold text-orange-900 mb-2">🚒 Fire Department</h4>
                    <p className="text-sm text-orange-800">{jha.emergencyInfo.fire.name}</p>
                    <p className="text-sm text-orange-700">{jha.emergencyInfo.fire.address}</p>
                    <p className="text-sm text-orange-700">Emergency: <span className="font-semibold">{jha.emergencyInfo.fire.phone}</span></p>
                    {jha.emergencyInfo.fire.nonEmergency != null && (
                      <p className="text-sm text-orange-700">Non-Emergency: {jha.emergencyInfo.fire.nonEmergency}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Utilities */}
              {jha.emergencyInfo.utilities && (
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">🔧 Local Utilities</h4>
                  <div className="grid md:grid-cols-3 gap-3">
                    {jha.emergencyInfo.utilities.electric && jha.emergencyInfo.utilities.electric.name && (
                      <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                        <p className="text-xs text-yellow-600 font-semibold">⚡ Electric</p>
                        <p className="text-sm text-yellow-900">{jha.emergencyInfo.utilities.electric.name}</p>
                        <p className="text-sm text-yellow-700">{jha.emergencyInfo.utilities.electric.phone}</p>
                      </div>
                    )}
                    {jha.emergencyInfo.utilities.gas && jha.emergencyInfo.utilities.gas.name && (
                      <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                        <p className="text-xs text-blue-600 font-semibold">🔥 Gas</p>
                        <p className="text-sm text-blue-900">{jha.emergencyInfo.utilities.gas.name}</p>
                        <p className="text-sm text-blue-700">{jha.emergencyInfo.utilities.gas.phone}</p>
                      </div>
                    )}
                    {jha.emergencyInfo.utilities.water && jha.emergencyInfo.utilities.water.name && (
                      <div className="bg-cyan-50 rounded-lg p-3 border border-cyan-200">
                        <p className="text-xs text-cyan-600 font-semibold">💧 Water</p>
                        <p className="text-sm text-cyan-900">{jha.emergencyInfo.utilities.water.name}</p>
                        <p className="text-sm text-cyan-700">{jha.emergencyInfo.utilities.water.phone}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(jha.emergencyInfo.evacuationPoint || jha.emergencyInfo.communicationPoint) && (
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  {jha.emergencyInfo.evacuationPoint && (
                    <div>
                      <label className="text-sm text-gray-500">Evacuation Point</label>
                      <p className="text-base font-medium text-gray-900">{jha.emergencyInfo.evacuationPoint}</p>
                    </div>
                  )}
                  {jha.emergencyInfo.communicationPoint && (
                    <div>
                      <label className="text-sm text-gray-500">Communication Point</label>
                      <p className="text-base font-medium text-gray-900">{jha.emergencyInfo.communicationPoint}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Job Site Exposures */}
          {(physicalHazards.length > 0 || healthHazards.length > 0 || oldHazards.length > 0) && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:shadow-none print:border-gray-300">
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="h-6 w-6 text-orange-600" />
                <h2 className="text-xl font-bold text-gray-900">Job Site Exposures</h2>
              </div>

              {physicalHazards.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Zap className="h-5 w-5 text-yellow-600" />
                    Physical Hazards
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {physicalHazards.map((h: string) => (
                      <span key={h} className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                        {PHYSICAL_HAZARD_NAMES[h] || h}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {healthHazards.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Health Hazards</h3>
                  <div className="flex flex-wrap gap-2">
                    {healthHazards.map((h: string) => (
                      <span key={h} className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                        {HEALTH_HAZARD_NAMES[h] || h}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Old format hazards */}
              {oldHazards.length > 0 && (
                <div className="space-y-2">
                  {oldHazards.map((hazard: any, index: number) => (
                    <div key={index} className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-gray-900">
                          {hazard.name || hazard.description || `Hazard ${index + 1}`}
                        </div>
                        {hazard.category && (
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            hazard.category === 'Critical' ? 'bg-red-100 text-red-800' :
                            hazard.category === 'High' ? 'bg-orange-100 text-orange-800' :
                            hazard.category === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {hazard.category}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Hazard Control Measures */}
          {(ppeControls.length > 0 || inspectionControls.length > 0 || oldControls.length > 0) && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:shadow-none print:border-gray-300">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="h-6 w-6 text-green-600" />
                <h2 className="text-xl font-bold text-gray-900">Hazard Control Measures</h2>
              </div>

              {ppeControls.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <HardHat className="h-5 w-5 text-blue-600" />
                    PPE Required
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {ppeControls.map((p: string) => (
                      <span key={p} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        {PPE_NAMES[p] || p}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {inspectionControls.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Eye className="h-5 w-5 text-purple-600" />
                    Inspections
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {inspectionControls.map((i: string) => (
                      <span key={i} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                        {INSPECTION_NAMES[i] || i}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {jha.controls && !Array.isArray(jha.controls) && jha.controls.additionalConsiderations && (
                <div>
                  <label className="text-sm text-gray-500">Additional Considerations</label>
                  <p className="text-base text-gray-900 mt-1">{jha.controls.additionalConsiderations}</p>
                </div>
              )}

              {/* Old format controls */}
              {oldControls.length > 0 && (
                <div className="space-y-2">
                  {oldControls.map((control: any, index: number) => (
                    <div key={index} className="flex items-start gap-3 border border-green-200 rounded-lg p-4 bg-green-50">
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div className="text-gray-900">
                        {control.description || control}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tasks, Hazards & Controls */}
          {tasks.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:shadow-none print:border-gray-300">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="h-6 w-6 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-900">Tasks, Hazards and Controls</h2>
              </div>
              <div className="space-y-4">
                {tasks.map((task, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="font-semibold text-gray-900 mb-3">Task #{index + 1}</div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm text-gray-500">Task</label>
                        <p className="text-sm font-medium text-gray-900">{task.task}</p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500">Hazards</label>
                        <p className="text-sm text-gray-900">{task.hazards}</p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500">Controls</label>
                        <p className="text-sm text-gray-900">{task.controls}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Multi-Employer Acknowledgment */}
          {jha.multiEmployerAcknowledged && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:shadow-none print:border-gray-300">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Multi-Employer Work Sites</h2>
                  <p className="text-sm text-gray-600">Requirements acknowledged and understood</p>
                </div>
              </div>
            </div>
          )}

          {/* RF Safety Plan */}
          {jha.rfSafety && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:shadow-none print:border-gray-300">
              <div className="flex items-center gap-3 mb-6">
                <Radio className="h-6 w-6 text-purple-600" />
                <h2 className="text-xl font-bold text-gray-900">RF Safety Plan</h2>
              </div>

              {jha.rfSafety.plan && (
                <div className="mb-6">
                  <label className="text-sm text-gray-500">RF Safety Plan</label>
                  <p className="text-sm text-gray-900 mt-1">{jha.rfSafety.plan}</p>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <h4 className="font-semibold text-purple-900 mb-2">Ground Level Monitoring</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-purple-600">Monitor S/N:</span> {jha.rfSafety.groundMonitorSN || 'N/A'}</p>
                    <p><span className="text-purple-600">MPE %:</span> {jha.rfSafety.groundMPE || 'N/A'}</p>
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <h4 className="font-semibold text-purple-900 mb-2">Structure Level Monitoring</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-purple-600">Monitor S/N:</span> {jha.rfSafety.structureMonitorSN || 'N/A'}</p>
                    <p><span className="text-purple-600">MPE %:</span> {jha.rfSafety.structureMPE || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {jha.rfSafety.controlMethods && jha.rfSafety.controlMethods.length > 0 && (
                <div className="mt-4">
                  <label className="text-sm text-gray-500">Control Methods</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {jha.rfSafety.controlMethods.map((m: string) => (
                      <span key={m} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                        {RF_CONTROL_NAMES[m] || m}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Civil Work */}
          {jha.civilWork && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:shadow-none print:border-gray-300">
              <div className="flex items-center gap-3 mb-6">
                <Shovel className="h-6 w-6 text-amber-600" />
                <h2 className="text-xl font-bold text-gray-900">Civil Work</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Electrical</h4>
                  <p className="text-sm text-gray-700">
                    De-Energized: {jha.civilWork.electricalDeEnergized ? 'Yes' : 'No'} | 
                    Energized: {jha.civilWork.electricalEnergized ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">811 Notified</h4>
                  <p className="text-sm text-gray-700">{jha.civilWork.is811Notified}</p>
                </div>
              </div>

              {jha.civilWork.excavationType && (
                <div className="mt-4">
                  <label className="text-sm text-gray-500">Excavation Type & Depth</label>
                  <p className="text-base text-gray-900">{jha.civilWork.excavationType}</p>
                </div>
              )}

              {jha.civilWork.caveInControls && (
                <div className="mt-4">
                  <label className="text-sm text-gray-500">Cave-In Control Measures</label>
                  <p className="text-base text-gray-900">{jha.civilWork.caveInControls}</p>
                </div>
              )}
            </div>
          )}

          {/* Project Personnel */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:shadow-none print:border-gray-300">
            <div className="flex items-center gap-3 mb-6">
              <Users className="h-6 w-6 text-indigo-600" />
              <h2 className="text-xl font-bold text-gray-900">Project Personnel ({crewMembers.length})</h2>
            </div>
            <div className="space-y-4">
              {crewMembers.map((member: any, index: number) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <UserCheck className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">
                          {member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim() || `Crew Member ${index + 1}`}
                          {/* Qwen audit issue 4: accountless visitor variant — spec
                              (locked decision): visitors show name/company/contact,
                              NO QR/badge. Always label the variant explicitly. */}
                          {member.userId == null && (
                            <span
                              data-testid="visitor-signer"
                              className="ml-2 align-middle inline-block rounded text-[10px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-800"
                            >
                              VISITOR
                            </span>
                          )}
                        </div>
                        {member.company && (
                          <div className="text-sm text-gray-600 mt-0.5">{member.company}</div>
                        )}
                        {member.credentials && member.credentials.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {member.credentials.map((cred: string, i: number) => (
                              <span key={i} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs">
                                {cred}
                              </span>
                            ))}
                          </div>
                        )}
                        {member.email && (
                          <div className="text-sm text-gray-600 flex items-center gap-1 mt-0.5">
                            {member.email}
                          </div>
                        )}
                        {member.phone && (
                          <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                            <Phone className="h-3 w-3" />
                            {member.phone}
                          </div>
                        )}
                      </div>
                    </div>
                    {member.signature && (
                      <div className="text-right">
                        <label className="text-xs text-gray-500">Signature</label>
                        <p className="text-lg font-signature text-gray-900">{member.signature}</p>
                      </div>
                    )}
                  </div>
                  {member.badge && member.badge.qrCode && (
                    <div className="mt-3 pt-3 border-t border-gray-200 flex justify-end" data-testid="crew-badge">
                      <CertBadge
                        name={member.badge.name}
                        role={member.badge.role}
                        photoUrl={member.badge.photoUrl}
                        qrCode={member.badge.qrCode}
                        certs={member.badge.certs}
                        variant={crewMembers.length > 4 ? 'compact' : 'full'}
                        print
                      />
                    </div>
                  )}
                  {/* WD-T2 (C): due-today equipment on the JHA detail — account-linked
                      crew only. Tier-1 one-tap inspections link back to this JHA
                      (jhaId); the Tier-2 kit is ONE pre-use record for the kit. */}
                  {member.userId != null && (!viewerIsWorker || member.userId === viewer.id) && (
                    <DueEquipmentChecklist
                      userId={member.userId}
                      crewLabel={member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim() || `Crew Member ${index + 1}`}
                      jhaId={jha.id}
                      canRecordKitCheck={canRecordKitCheck}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Site Visitors */}
          {siteVisitors.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:shadow-none print:border-gray-300">
              <div className="flex items-center gap-3 mb-6">
                <Users className="h-6 w-6 text-gray-600" />
                <h2 className="text-xl font-bold text-gray-900">Site Visitors</h2>
              </div>
              <ul className="list-disc list-inside space-y-1">
                {siteVisitors.map((visitor, index) => (
                  <li key={index} className="text-gray-700">{visitor}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Signature Footer */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:shadow-none print:border-gray-300">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-500">Created By</div>
                <div className="text-lg font-bold text-gray-900 mt-1">
                  {jha.createdBy.firstName} {jha.createdBy.lastName}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {new Date(jha.signedAt).toLocaleString()}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Document ID</div>
                <div className="text-lg font-mono font-medium text-gray-900 mt-1">
                  JHA-{String(jha.id).padStart(6, '0')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 print:hidden">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Mail className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Email JHA Document</h3>
                <p className="text-sm text-gray-600">Send a PDF copy via email</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Email
                </label>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="Enter email address..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Document:</strong> JHA-{String(jha.id).padStart(6, '0')}<br />
                  <strong>Project:</strong> {jha.project.name}<br />
                  <strong>Date:</strong> {formatCalendarDate(jha.date)}
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEmailSend}
                  disabled={emailSending}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 transition flex items-center justify-center gap-2"
                >
                  {emailSending ? (
                    <>
                      <Clock className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send Email
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
